import { useState, type CSSProperties, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { OwnerEntity, UserRole, type UserSummaryDto } from '@bhops/shared';
import { usersApi, type CreateUserBody, type UpdateUserBody } from '@/api/users';
import { extractApiError } from '@/api/client';
import { kstDateTime } from '@/lib/format-date';
import { Modal } from '@/components/Modal';
import { Button, Field, FormError, Input, Select } from '@/components/Form';
import { useToast } from '@/components/Toast';
import { ConfirmDialog } from '@/components/ConfirmDialog';

const ROLE_LABEL: Record<UserRole, string> = {
  ADMIN: '관리자',
  MANAGER: '매니저(옛 역할)',
  SALES_MANAGER: '사업팀 매니저',
  MFG_MANAGER: '제조팀 매니저',
  FIELD_MANAGER: '필드팀 매니저',
  VIEWER: '뷰어',
};

const OWNER_LABEL: Record<OwnerEntity, string> = {
  [OwnerEntity.HONEYCOMB_ROBOTICS]: '허니컴로보틱스',
  [OwnerEntity.BEYOND_HONEYCOMB]: '비욘드허니컴',
};

type UserRow = { id: string; displayName: string; disabledAt?: string | null };

export function UsersPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<UserSummaryDto | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UserSummaryDto | null>(null);
  const [toggleTarget, setToggleTarget] = useState<UserRow | null>(null);
  const qc = useQueryClient();
  const toast = useToast();

  const { data, isLoading, error } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
  });

  const toggleDisabled = useMutation({
    mutationFn: (args: { id: string; disabled: boolean }) =>
      usersApi.update(args.id, { disabled: args.disabled }),
    onSuccess: (_, args) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      toast.success(args.disabled ? '사용자가 비활성화되었습니다.' : '사용자가 활성화되었습니다.');
    },
    onError: (err) => toast.error(extractApiError(err), '상태 변경 실패'),
  });

  // FB §1 — soft-delete. 성공 시 목록 invalidate (서버가 deletedAt 행을 제외).
  const removeUser = useMutation({
    mutationFn: (id: string) => usersApi.remove(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      const name = data?.find((u) => u.id === id)?.displayName ?? '사용자';
      toast.success(`${name} 계정을 삭제했습니다.`);
    },
    onError: (err) => toast.error(extractApiError(err), '삭제 실패'),
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <h1 className="kuun-h2" style={{ margin: 0 }}>
          사용자
        </h1>
        <Button type="button" style={{ marginLeft: 'auto' }} onClick={() => setCreateOpen(true)}>
          + 사용자 추가
        </Button>
      </div>

      <div
        style={{
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--r-card)',
          background: 'var(--bg-surface)',
          overflow: 'auto',
        }}
      >
        {isLoading ? (
          <div style={empty}>
            <span className="kuun-caption">불러오는 중…</span>
          </div>
        ) : error ? (
          <div style={empty}>
            <span style={{ color: 'var(--fg-danger)' }}>{extractApiError(error)}</span>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                <th style={th}>이름</th>
                <th style={th}>이메일</th>
                <th style={th}>역할</th>
                <th style={th}>법인</th>
                <th style={th}>마지막 로그인</th>
                <th style={th}>상태</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {data?.map((u) => (
                <tr key={u.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                  <td style={td}>{u.displayName}</td>
                  <td style={td}>{u.email}</td>
                  <td style={td}>{ROLE_LABEL[u.role]}</td>
                  <td style={td}>{OWNER_LABEL[u.ownerEntity]}</td>
                  <td style={{ ...td, fontFamily: 'var(--font-mono)' }}>
                    {/* 추가수정 2차(26-07-27) §2 — 브라우저 로캘 대신 KST 고정.
                        로그인 시각은 시스템이 찍는 값이라 시각까지 남긴다. */}
                    {kstDateTime(u.lastLoginAt) ?? '–'}
                  </td>
                  <td style={td}>
                    {u.disabledAt ? (
                      <span style={{ color: 'var(--fg-danger)' }}>비활성</span>
                    ) : (
                      <span style={{ color: 'var(--robot-operating)' }}>활성</span>
                    )}
                  </td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <Button type="button" variant="secondary" onClick={() => setEditTarget(u)}>
                        편집
                      </Button>
                      <Button type="button" variant="secondary" onClick={() => setToggleTarget(u)}>
                        {u.disabledAt ? '활성화' : '비활성화'}
                      </Button>
                      <Button type="button" variant="danger" onClick={() => setDeleteTarget(u)}>
                        삭제
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <UserCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />

      <UserEditModal user={editTarget} onClose={() => setEditTarget(null)} />

      <ConfirmDialog
        open={!!deleteTarget}
        title="사용자 삭제"
        message={
          deleteTarget
            ? `${deleteTarget.displayName} 계정을 삭제할까요? 이 계정은 바로 로그아웃되고 목록에서 사라집니다.`
            : ''
        }
        confirmLabel="삭제"
        danger
        busy={removeUser.isPending}
        onConfirm={() => {
          if (deleteTarget) removeUser.mutate(deleteTarget.id);
          setDeleteTarget(null);
        }}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={!!toggleTarget}
        title={toggleTarget?.disabledAt ? '사용자 활성화' : '사용자 비활성화'}
        message={
          toggleTarget?.disabledAt
            ? `${toggleTarget.displayName} 계정을 활성화하시겠습니까?`
            : `${toggleTarget?.displayName} 계정을 비활성화하시겠습니까?`
        }
        confirmLabel={toggleTarget?.disabledAt ? '활성화' : '비활성화'}
        danger={!toggleTarget?.disabledAt}
        busy={toggleDisabled.isPending}
        onConfirm={() => {
          if (toggleTarget) {
            toggleDisabled.mutate({ id: toggleTarget.id, disabled: !toggleTarget.disabledAt });
          }
          setToggleTarget(null);
        }}
        onCancel={() => setToggleTarget(null)}
      />
    </div>
  );
}

function UserCreateModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState<CreateUserBody>({
    email: '',
    password: '',
    displayName: '',
    role: UserRole.VIEWER,
    ownerEntity: OwnerEntity.BEYOND_HONEYCOMB,
  });
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (body: CreateUserBody) => usersApi.create(body),
    onSuccess: (user) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      toast.success(`${user.displayName} 님을 추가했습니다.`, '사용자 등록');
      onClose();
      setForm({
        email: '',
        password: '',
        displayName: '',
        role: UserRole.VIEWER,
        ownerEntity: OwnerEntity.BEYOND_HONEYCOMB,
      });
    },
    onError: (err) => setError(extractApiError(err)),
  });

  function submit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setError(null);
    create.mutate(form);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="새 사용자"
      width={500}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button type="submit" form="user-create-form" disabled={create.isPending}>
            {create.isPending ? '저장 중…' : '등록'}
          </Button>
        </>
      }
    >
      <form id="user-create-form" onSubmit={submit} noValidate>
        <FormError>{error}</FormError>

        <Field label="이메일" htmlFor="u-email" required>
          <Input
            id="u-email"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
          />
        </Field>

        <Field label="표시 이름" htmlFor="u-name" required>
          <Input
            id="u-name"
            value={form.displayName}
            onChange={(e) => setForm({ ...form, displayName: e.target.value })}
            required
          />
        </Field>

        <Field
          label="비밀번호"
          htmlFor="u-pw"
          required
          hint="8자 이상으로 정해 주세요. 처음 로그인한 뒤 바꾸는 것이 좋습니다."
        >
          <Input
            id="u-pw"
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            required
            minLength={8}
          />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="역할" htmlFor="u-role">
            <Select
              id="u-role"
              value={form.role ?? UserRole.VIEWER}
              onChange={(e) => setForm({ ...form, role: e.target.value as UserRole })}
            >
              <option value={UserRole.ADMIN}>관리자</option>
              <option value={UserRole.SALES_MANAGER}>사업팀 매니저</option>
              <option value={UserRole.MFG_MANAGER}>제조팀 매니저</option>
              <option value={UserRole.FIELD_MANAGER}>필드팀 매니저</option>
              <option value={UserRole.MANAGER} disabled>
                매니저(옛 역할, 새 사용자는 위의 팀별 매니저로 정해 주세요)
              </option>
              <option value={UserRole.VIEWER}>뷰어</option>
            </Select>
          </Field>

          <Field label="법인" htmlFor="u-entity">
            <Select
              id="u-entity"
              value={form.ownerEntity}
              onChange={(e) => setForm({ ...form, ownerEntity: e.target.value as OwnerEntity })}
            >
              <option value={OwnerEntity.BEYOND_HONEYCOMB}>비욘드허니컴</option>
              <option value={OwnerEntity.HONEYCOMB_ROBOTICS}>허니컴로보틱스</option>
            </Select>
          </Field>
        </div>
      </form>
    </Modal>
  );
}

// FB §1 — 사용자 편집 모달. UserCreateModal 을 미러하되 이메일/비밀번호는
// 제외하고 이름·역할·법인(ownerEntity)만 PATCH. user 가 null 이면 닫힘 상태.
function UserEditModal({ user, onClose }: { user: UserSummaryDto | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);

  const update = useMutation({
    mutationFn: (args: { id: string; body: UpdateUserBody }) => usersApi.update(args.id, args.body),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      toast.success(`${updated.displayName} 정보를 수정했습니다.`, '사용자 편집');
      onClose();
    },
    onError: (err) => setError(extractApiError(err)),
  });

  function submit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setError(null);
    const form = e.currentTarget;
    const displayName = (form.elements.namedItem('displayName') as HTMLInputElement).value.trim();
    const role = (form.elements.namedItem('role') as HTMLSelectElement).value as UserRole;
    const ownerEntity = (form.elements.namedItem('ownerEntity') as HTMLSelectElement)
      .value as OwnerEntity;
    if (user) update.mutate({ id: user.id, body: { displayName, role, ownerEntity } });
  }

  return (
    <Modal
      open={!!user}
      onClose={onClose}
      title="사용자 편집"
      width={500}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button type="submit" form="user-edit-form" disabled={update.isPending}>
            {update.isPending ? '저장 중…' : '저장'}
          </Button>
        </>
      }
    >
      {user && (
        // key=user.id 로 사용자 전환 시 defaultValue 가 새 행 값으로 재마운트.
        <form id="user-edit-form" key={user.id} onSubmit={submit} noValidate>
          <FormError>{error}</FormError>

          <Field label="이메일" htmlFor="ue-email">
            <Input id="ue-email" type="email" value={user.email} disabled readOnly />
          </Field>

          <Field label="표시 이름" htmlFor="ue-name" required>
            <Input id="ue-name" name="displayName" defaultValue={user.displayName} required />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="역할" htmlFor="ue-role">
              <Select id="ue-role" name="role" defaultValue={user.role}>
                <option value={UserRole.ADMIN}>관리자</option>
                <option value={UserRole.SALES_MANAGER}>사업팀 매니저</option>
                <option value={UserRole.MFG_MANAGER}>제조팀 매니저</option>
                <option value={UserRole.FIELD_MANAGER}>필드팀 매니저</option>
                <option value={UserRole.MANAGER} disabled>
                  매니저(옛 역할)
                </option>
                <option value={UserRole.VIEWER}>뷰어</option>
              </Select>
            </Field>

            <Field label="법인" htmlFor="ue-entity">
              <Select id="ue-entity" name="ownerEntity" defaultValue={user.ownerEntity}>
                <option value={OwnerEntity.BEYOND_HONEYCOMB}>비욘드허니컴</option>
                <option value={OwnerEntity.HONEYCOMB_ROBOTICS}>허니컴로보틱스</option>
              </Select>
            </Field>
          </div>
        </form>
      )}
    </Modal>
  );
}

const th: CSSProperties = {
  textAlign: 'left',
  padding: '10px 12px',
  fontWeight: 600,
  color: 'var(--fg-muted)',
  background: 'var(--bg-sunken)',
  borderBottom: '1px solid var(--border-subtle)',
};

const td: CSSProperties = { padding: '10px 12px', verticalAlign: 'middle' };
const empty: CSSProperties = { padding: 40, textAlign: 'center' };
